import { RoadWriter } from "./road-authoring.js";

const DESCRIPTIONS = {
  waterfall: [
    "Learn the frozen descent through broad ice shelves and a readable first drop.",
    "Link offset glacier platforms while the slope steadily builds speed.",
    "Thread split ice routes while whirlpools pull at exposed landing zones.",
    "Commit to long waterfall drops and small two-by-two recovery platforms.",
    "Survive the full frozen cascade: steep descents, curtains, whirlpools and precision ice jumps.",
  ],
  redheat: [
    "Read the first heat beams and cross cooling lanes before the floor ignites.",
    "Carve around magma bends where crumble paths punish late steering.",
    "Cross alternating ash lanes while beams and broken flooring compress the safe route.",
    "Climb a narrow molten spine through chained hazards and collapsing landings.",
    "Breach the core through the longest heat sequence, with almost no safe straightaways.",
  ],
  asteroid: [
    "A readable drift line introduces meteor warnings beside broad void crossings.",
    "Choose mining cuts through split rock shelves and unstable crumble routes.",
    "Debris narrows the road while meteor strikes deny the obvious landing lane.",
    "Changing elevation and scattered platforms turn gravity into part of the route.",
    "Break through the belt on long precision jumps with meteors closing every recovery line.",
  ],
  blueplanet: [
    "Ride a broad tidal path while learning the rhythm of the first geysers.",
    "Reef curves mix ice, flowing lines and offset two-lane landings.",
    "Mist hides geyser timing across split paths and stepped water platforms.",
    "A fast crystal current chains slippery bends with narrow eruption windows.",
    "Descend into the abyss through the world's longest ice-and-geyser examination.",
  ],
  satellite: [
    "Enter the docking lane through slow, readable scan gates.",
    "Panel runs narrow the route and place solid obstacles behind moving scans.",
    "Signal relays demand deliberate lane changes between consecutive gate sweeps.",
    "Orbital switchbacks combine precision bends, gaps and scan timing.",
    "Reach transmission apex through a long sequence of one-lane gates and exposed jumps.",
  ],
  misty: [
    "Enter the fog on broad lanes with isolated ghost barriers.",
    "Silent pillars materialise beside narrowing paths and late bends.",
    "Veil crossings mix hidden walls with offset jump landings.",
    "Memorise a shifting route through dense fog, splits and chained ghost barriers.",
    "Climb the phantom summit with minimal visibility and almost no recovery space.",
  ],
  nebula: [
    "Follow the starwake through broad arcs and glowing split routes.",
    "Ion drift introduces charged crossings on faster, narrower bends.",
    "Weave through lightning, crumble strips and alternating jump lanes.",
    "The pulse corridor chains plasma timing with sustained high-speed precision.",
    "Claim the supernova crown through the hardest main-world route in Orbitrush.",
  ],
};

function gapFor(roadIndex, stage, bias = 0) {
  return Math.min(3, 1 + Math.floor((roadIndex + stage + bias) / 4));
}

function alternatingOffset(stage, magnitude) {
  return (stage % 2 === 0 ? 1 : -1) * magnitude;
}

function buildWaterfall(id, roadIndex) {
  const w = new RoadWriter(id, "waterfall");
  let height = 5.5 + roadIndex * 1.4;
  let offset = 0;
  const stages = 4 + roadIndex;
  w.ramp("iiiii", Math.ceil(height / 0.38) + 1, 0, height)
    .setpiece("icefall", -1)
    .add("iibii", 2, { _elevation: height });

  for (let stage = 0; stage < stages; stage += 1) {
    const next = alternatingOffset(stage, 1.35 + roadIndex * 0.15);
    const drop = 0.65 + roadIndex * 0.16 + (stage % 3) * 0.2;
    const nextHeight = Math.max(0.8, height - drop);
    if (stage % 4 === 0) {
      w.bendAndRamp("iiiii", 12, offset, next, height, nextHeight)
        .add("ii.ii", 4, { _offset: next, _elevation: nextHeight })
        .hazard("iiiii", 3, "curtain", 1 + (stage % 3), {
          _offset: next,
          _elevation: nextHeight,
        });
    } else if (stage % 4 === 1) {
      w.add(".ii..", 4, { _offset: offset, _elevation: height })
        .jump("..ii.", gapFor(roadIndex, stage), {
          _offset: next,
          _elevation: nextHeight,
        }, ".ib..")
        .add("..ii.", 4, { _offset: next, _elevation: nextHeight });
    } else if (stage % 4 === 2) {
      w.curve(".iii.", 11, offset, next, { _elevation: height })
        .hazard(".iii.", 4, "whirlpool", 2 + (stage % 2), {
          _offset: next,
          _elevation: height,
        })
        .add(".iui.", 2, { _offset: next, _elevation: height })
        .setpiece("whirlpool", stage % 2 ? -1 : 1);
    } else {
      w.bendAndRamp("ii.ii", 10, offset, next, height, nextHeight)
        .add("ib.bi", 1, { _offset: next, _elevation: nextHeight })
        .gap(gapFor(roadIndex, stage, 1), {
          _offset: next,
          _elevation: nextHeight,
        })
        .add("ii.ii", 4, { _offset: next, _elevation: nextHeight })
        .setpiece("icefall", stage % 2 ? 1 : -1);
    }
    offset = next;
    height = nextHeight;
  }

  w.bendAndRamp("iiiii", 12, offset, 0, height, 0)
    .add("iiuii", 2)
    .jump("iiiii", Math.min(3, 1 + Math.floor(roadIndex / 2)), {}, "iibii")
    .finish();
  return { rows: w.rows, desc: DESCRIPTIONS.waterfall[roadIndex] };
}

function buildRedHeat(id, roadIndex) {
  const w = new RoadWriter(id, "redheat");
  let offset = 0;
  let height = 0;
  const stages = 5 + roadIndex;
  w.add("nnnnn", 9)
    .setpiece("lava-vent", 1)
    .hazard("nnnnn", 3, "beam", 1, {});

  for (let stage = 0; stage < stages; stage += 1) {
    const next = alternatingOffset(stage, 1.5 + roadIndex * 0.13);
    if (stage % 4 === 0) {
      w.curve("nnnnn", 11, offset, next, { _elevation: height })
        .add("ncdcn", 3, { _offset: next, _elevation: height })
        .hazard("nnnnn", 3, "beam", stage % 3, {
          _offset: next,
          _elevation: height,
        });
    } else if (stage % 4 === 1) {
      const nextHeight = height + 1.2 + roadIndex * 0.16;
      w.bendAndRamp("nn.nn", 12, offset, next, height, nextHeight)
        .add("cc.cc", 4, { _offset: next, _elevation: nextHeight })
        .jump("nn.nn", gapFor(roadIndex, stage), {
          _offset: next,
          _elevation: nextHeight,
        }, "bb.bb");
      height = nextHeight;
    } else if (stage % 4 === 2) {
      w.add("n.n.n", 5, { _offset: offset, _elevation: height })
        .hazard("n.n.n", 3, "beam", 2, {
          _offset: offset,
          _elevation: height,
        })
        .add("b.b.b", 1, { _offset: offset, _elevation: height })
        .gap(gapFor(roadIndex, stage), {
          _offset: offset,
          _elevation: height,
        })
        .curve("n.n.n", 8, offset, next, { _elevation: height })
        .setpiece("magma-arch", stage % 2 ? -1 : 1);
    } else {
      const nextHeight = Math.max(0, height - 1.1);
      w.bendAndRamp(".nnn.", 12, offset, next, height, nextHeight)
        .add(".dcd.", 3, { _offset: next, _elevation: nextHeight })
        .hazard(".nnn.", 3, "beam", 1 + (stage % 2), {
          _offset: next,
          _elevation: nextHeight,
        })
        .add(".uuu.", 2, { _offset: next, _elevation: nextHeight });
      height = nextHeight;
    }
    offset = next;
  }

  w.bendAndRamp("nnnnn", 12, offset, 0, height, 0)
    .hazard("ndndn", 3, "beam", 1 + (roadIndex % 2), {})
    .jump("nnnnn", Math.min(3, 1 + Math.floor(roadIndex / 2)))
    .finish();
  return { rows: w.rows, desc: DESCRIPTIONS.redheat[roadIndex] };
}

function buildAsteroid(id, roadIndex) {
  const w = new RoadWriter(id, "asteroid");
  let offset = 0;
  let height = 0;
  const stages = 5 + roadIndex;
  w.add("nnnnn", 8).setpiece("asteroid-field", -1);

  for (let stage = 0; stage < stages; stage += 1) {
    const next = alternatingOffset(stage, 1.7 + roadIndex * 0.14);
    if (stage % 4 === 0) {
      w.curve(".nnn.", 11, offset, next, { _elevation: height })
        .hazard(".nnn.", 3, "meteor", 1 + (stage % 3), {
          _offset: next,
          _elevation: height,
        })
        .add(".n.n.", 4, { _offset: next, _elevation: height });
    } else if (stage % 4 === 1) {
      w.add("nn.nn", 4, { _offset: offset, _elevation: height })
        .jump("nn.nn", gapFor(roadIndex, stage, 1), {
          _offset: next,
          _elevation: height,
        }, "bb.bb")
        .hazard("nn.nn", 3, "meteor", stage % 5, {
          _offset: next,
          _elevation: height,
        });
    } else if (stage % 4 === 2) {
      const nextHeight = height + (stage % 2 ? -1 : 1) * (1.1 + roadIndex * 0.14);
      w.bendAndRamp("n.n.n", 13, offset, next, height, nextHeight)
        .add("c.c.c", 3, { _offset: next, _elevation: nextHeight })
        .jump("n.n.n", gapFor(roadIndex, stage), {
          _offset: next,
          _elevation: nextHeight,
        }, "b.b.b");
      height = nextHeight;
    } else {
      w.add("..n..", 5, { _offset: offset, _elevation: height })
        .hazard("..n..", 3, "meteor", 2, {
          _offset: offset,
          _elevation: height,
        })
        .jump(".nn..", gapFor(roadIndex, stage, 1), {
          _offset: next,
          _elevation: height,
        }, "..b..")
        .add(".n.n.", 4, { _offset: next, _elevation: height })
        .setpiece("mining-rig", stage % 2 ? 1 : -1);
    }
    offset = next;
  }

  w.bendAndRamp("nnnnn", 12, offset, 0, height, 0)
    .add("nunun", 2)
    .hazard("nnnnn", 3, "meteor", 2, {})
    .jump("nnnnn", Math.min(3, 1 + Math.floor(roadIndex / 2)))
    .finish();
  return { rows: w.rows, desc: DESCRIPTIONS.asteroid[roadIndex] };
}

function buildBluePlanet(id, roadIndex) {
  const w = new RoadWriter(id, "blueplanet");
  let offset = 0;
  let height = 2 + roadIndex * 0.45;
  const stages = 6 + roadIndex;
  w.ramp("nnnnn", Math.ceil(height / 0.38) + 1, 0, height).setpiece("reef-fall", 1);

  for (let stage = 0; stage < stages; stage += 1) {
    const next = alternatingOffset(stage, 1.55 + roadIndex * 0.13);
    const surface = stage % 3 === 0 ? "iiiii" : "nnnnn";
    if (stage % 4 === 0) {
      w.curve(surface, 12, offset, next, { _elevation: height })
        .hazard(surface, 3, "geyser", 1 + (stage % 3), {
          _offset: next,
          _elevation: height,
        })
        .add("ii.ii", 4, { _offset: next, _elevation: height });
    } else if (stage % 4 === 1) {
      const nextHeight = Math.max(0, height - 0.7);
      w.bendAndRamp(".iii.", 11, offset, next, height, nextHeight)
        .add(".iui.", 2, { _offset: next, _elevation: nextHeight })
        .jump(".ii..", gapFor(roadIndex, stage), {
          _offset: next,
          _elevation: nextHeight,
        }, ".ib..")
        .add("..ii.", 4, { _offset: next, _elevation: nextHeight });
      height = nextHeight;
    } else if (stage % 4 === 2) {
      w.add("nn.nn", 5, { _offset: offset, _elevation: height })
        .hazard("nn.nn", 3, "geyser", stage % 5, {
          _offset: offset,
          _elevation: height,
        })
        .curve("ii.ii", 10, offset, next, { _elevation: height })
        .setpiece("crystal-reef", stage % 2 ? -1 : 1);
    } else {
      w.add(".n.n.", 4, { _offset: offset, _elevation: height })
        .add(".b.b.", 1, { _offset: offset, _elevation: height })
        .gap(gapFor(roadIndex, stage, 1), {
          _offset: offset,
          _elevation: height,
        })
        .add(".i.i.", 4, { _offset: next, _elevation: height })
        .hazard(".i.i.", 3, "geyser", 1 + (stage % 3), {
          _offset: next,
          _elevation: height,
        });
    }
    offset = next;
  }

  w.bendAndRamp("iiiii", 12, offset, 0, height, 0)
    .add("iiuii", 2)
    .hazard("iiiii", 3, "geyser", 2, {})
    .jump("iiiii", Math.min(3, 1 + Math.floor(roadIndex / 2)), {}, "iibii")
    .finish();
  return { rows: w.rows, desc: DESCRIPTIONS.blueplanet[roadIndex] };
}

function buildSatellite(id, roadIndex) {
  const w = new RoadWriter(id, "satellite");
  let offset = 0;
  let height = 0;
  const stages = 6 + roadIndex;
  w.add("nnnnn", 9).setpiece("docking-array", -1);

  for (let stage = 0; stage < stages; stage += 1) {
    const next = alternatingOffset(stage, 1.65 + roadIndex * 0.12);
    if (stage % 4 === 0) {
      w.curve("nnnnn", 10, offset, next, { _elevation: height })
        .hazard("nnnnn", 4, "scan", stage % 5, {
          _offset: next,
          _elevation: height,
        })
        .add("nxnxn", 2, { _offset: next, _elevation: height });
    } else if (stage % 4 === 1) {
      w.add(".nnn.", 4, { _offset: offset, _elevation: height })
        .hazard(".nnn.", 3, "scan", 1 + (stage % 3), {
          _offset: offset,
          _elevation: height,
        })
        .jump(".nnn.", gapFor(roadIndex, stage), {
          _offset: next,
          _elevation: height,
        })
        .setpiece("scan-gate", stage % 2 ? 1 : -1);
    } else if (stage % 4 === 2) {
      const nextHeight = height + (stage % 2 ? -1 : 1) * 1.25;
      w.bendAndRamp("nn.nn", 13, offset, next, height, nextHeight)
        .add("cc.cc", 3, { _offset: next, _elevation: nextHeight })
        .hazard("nn.nn", 3, "scan", stage % 5, {
          _offset: next,
          _elevation: nextHeight,
        });
      height = nextHeight;
    } else {
      w.add("..n..", 5, { _offset: offset, _elevation: height })
        .hazard("..n..", 3, "scan", 2, {
          _offset: offset,
          _elevation: height,
        })
        .jump("n.n.n", gapFor(roadIndex, stage, 1), {
          _offset: next,
          _elevation: height,
        }, "..b..")
        .add("nunun", 2, { _offset: next, _elevation: height });
    }
    offset = next;
  }

  w.bendAndRamp("nnnnn", 12, offset, 0, height, 0)
    .hazard("nxnxn", 3, "scan", 2, {})
    .jump("nnnnn", Math.min(3, 1 + Math.floor(roadIndex / 2)))
    .finish();
  return { rows: w.rows, desc: DESCRIPTIONS.satellite[roadIndex] };
}

function buildMisty(id, roadIndex) {
  const w = new RoadWriter(id, "misty");
  let offset = 0;
  let height = 0.8;
  const stages = 7 + roadIndex;
  w.ramp("nnnnn", 8, 0, height).setpiece("mist-pillars", 1);

  for (let stage = 0; stage < stages; stage += 1) {
    const next = alternatingOffset(stage, 1.75 + roadIndex * 0.14);
    if (stage % 4 === 0) {
      w.curve(".nnn.", 12, offset, next, { _elevation: height })
        .hazard(".nnn.", 3, "ghost", 1 + (stage % 3), {
          _offset: next,
          _elevation: height,
        })
        .add(".n.n.", 4, { _offset: next, _elevation: height });
    } else if (stage % 4 === 1) {
      w.add("n.n.n", 5, { _offset: offset, _elevation: height })
        .hazard("n.n.n", 3, "ghost", stage % 5, {
          _offset: offset,
          _elevation: height,
        })
        .jump(".nn..", gapFor(roadIndex, stage), {
          _offset: next,
          _elevation: height,
        }, "b.b.b")
        .add("..nn.", 4, { _offset: next, _elevation: height });
    } else if (stage % 4 === 2) {
      const nextHeight = height + (stage % 3 === 0 ? 1.35 : -0.9);
      w.bendAndRamp("nn.nn", 14, offset, next, height, nextHeight)
        .hazard("nn.nn", 3, "ghost", 2, {
          _offset: next,
          _elevation: nextHeight,
        })
        .add("c...c", 3, { _offset: next, _elevation: nextHeight });
      height = nextHeight;
    } else {
      w.add("..n..", 5, { _offset: offset, _elevation: height })
        .hazard("..n..", 3, "ghost", 2, {
          _offset: offset,
          _elevation: height,
        })
        .jump("..n..", gapFor(roadIndex, stage, 1), {
          _offset: next,
          _elevation: height,
        }, "..b..")
        .add(".uuu.", 2, { _offset: next, _elevation: height })
        .setpiece("ghost-spires", stage % 2 ? -1 : 1);
    }
    offset = next;
  }

  w.bendAndRamp(".nnn.", 13, offset, 0, height, 0)
    .hazard(".n.n.", 3, "ghost", 2, {})
    .jump(".nnn.", Math.min(3, 1 + Math.floor(roadIndex / 2)))
    .finish();
  return { rows: w.rows, desc: DESCRIPTIONS.misty[roadIndex] };
}

function buildNebula(id, roadIndex) {
  const w = new RoadWriter(id, "nebula");
  let offset = 0;
  let height = 0;
  const stages = 8 + roadIndex;
  w.add("nnnnn", 8).setpiece("plasma-crown", -1).add("bbbbb", 1);

  for (let stage = 0; stage < stages; stage += 1) {
    const next = alternatingOffset(stage, 1.85 + roadIndex * 0.14);
    if (stage % 4 === 0) {
      w.curve("nnnnn", 12, offset, next, { _elevation: height })
        .hazard("nnnnn", 3, "arc", stage % 5, {
          _offset: next,
          _elevation: height,
        })
        .add("ndndn", 3, { _offset: next, _elevation: height });
    } else if (stage % 4 === 1) {
      w.add("nn.nn", 4, { _offset: offset, _elevation: height })
        .add("bb.bb", 1, { _offset: offset, _elevation: height })
        .gap(gapFor(roadIndex, stage, 1), {
          _offset: offset,
          _elevation: height,
        })
        .add("nn.nn", 4, { _offset: next, _elevation: height })
        .hazard("nn.nn", 3, "arc", 1 + (stage % 3), {
          _offset: next,
          _elevation: height,
        });
    } else if (stage % 4 === 2) {
      const nextHeight = height + (stage % 2 ? -1.1 : 1.45);
      w.bendAndRamp("n.n.n", 14, offset, next, height, nextHeight)
        .add("c.c.c", 3, { _offset: next, _elevation: nextHeight })
        .hazard("n.n.n", 3, "arc", stage % 5, {
          _offset: next,
          _elevation: nextHeight,
        });
      height = nextHeight;
    } else {
      w.add("..n..", 4, { _offset: offset, _elevation: height })
        .hazard("..n..", 3, "arc", 2, {
          _offset: offset,
          _elevation: height,
        })
        .jump(".n.n.", gapFor(roadIndex, stage, 2), {
          _offset: next,
          _elevation: height,
        }, "..b..")
        .add(".b.b.", 1, { _offset: next, _elevation: height })
        .setpiece("ion-storm", stage % 2 ? 1 : -1);
    }
    offset = next;
  }

  w.bendAndRamp("nnnnn", 14, offset, 0, height, 0)
    .hazard("n.c.n", 3, "arc", 2, {})
    .jump(".nnn.", Math.min(3, 2 + Math.floor(roadIndex / 3)))
    .add("bbbbb", 1)
    .finish();
  return { rows: w.rows, desc: DESCRIPTIONS.nebula[roadIndex] };
}

const BUILDERS = {
  waterfall: buildWaterfall,
  redheat: buildRedHeat,
  asteroid: buildAsteroid,
  blueplanet: buildBluePlanet,
  satellite: buildSatellite,
  misty: buildMisty,
  nebula: buildNebula,
};

export function buildWorldRoad(worldId, roadId, roadIndex) {
  return BUILDERS[worldId]?.(roadId, roadIndex) || null;
}
